import { all, call, put, takeLatest } from 'redux-saga/effects';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFarmsListRequest } from '../farms/farms.actions';
import { createRelation, deleteRelation, getRelations, updateRelation } from './relations.actions';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';

export function* getRelationsSaga(action: ReturnType<typeof getRelations.started>) {
  yield put(showLoader(LoadersNames.getRelations));
  try {
    const canReadRelations = yield call(checkPermissions, {
      resource: Resource.FEED_MANAGE_CONTACTS,
      action: Action.READ,
    });
    if (canReadRelations) {
      const result = yield call(api.relations.getRelations);
      yield put(
        getRelations.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getRelations));
}

export function* createRelationSaga(action: ReturnType<typeof createRelation.started>) {
  yield put(showLoader(LoadersNames.createRelation));
  try {
    const { payload, meta } = action;
    const { data, formName } = payload;
    const result = yield call(api.relations.createRelation, data, formName);
    yield put(
      createRelation.done({
        params: payload,
        result,
      }),
    );
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
      if (meta.successKey) {
        yield put(notificationSuccess(meta.successKey));
      }
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createRelation));
}

export function* updateRelationSaga(action: ReturnType<typeof updateRelation.started>) {
  try {
    const { payload, meta } = action;
    const { data, formName } = payload;
    const result = yield call(api.relations.updateRelation, data.id!, data, formName);
    yield put(
      updateRelation.done({
        params: payload,
        result,
      }),
    );
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
      if (meta.successKey) {
        yield put(notificationSuccess(meta.successKey));
      }
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* deleteRelationSaga(action: ReturnType<typeof deleteRelation.started>) {
  try {
    const { id, formName } = action.payload;
    yield call(api.relations.deleteRelation, id, formName);
    yield put(
      deleteRelation.done({
        params: action.payload,
        result: id,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('contacts:delete_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchRelationsSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, getRelationsSaga),
    yield takeLatest(getRelations.started, getRelationsSaga),
    yield takeLatest(createRelation.started, createRelationSaga),
    yield takeLatest(updateRelation.started, updateRelationSaga),
    yield takeLatest(deleteRelation.started, deleteRelationSaga),
  ]);
}
